import { useMemo } from "react";
import { useGetClientsQuery } from "@/redux/apis/client/clientApi";
import { useGetVehiclesQuery } from "@/redux/apis/vehicle/vehicleApi";
import { OnboardingStatus } from "@/redux/slices/onboarding/types";
import {
  clientStepParams,
  isClientStepComplete,
  isNetworkStepComplete,
  isPricingStepComplete,
  isVehicleStepComplete,
  vehicleStepParams,
} from "../helpers/soloStepCompletion";

/**
 * Completion state for the four Driver App steps, keyed by step number.
 *
 * Nothing in the web portal can action these steps — they tick off on their own once the operator
 * has done them on their phone. Steps 3 and 4 are derived from live data; steps 5 and 6 tick on
 * having visited the relevant Driver App screen, which the Driver App records against the
 * onboarding status because a visit is not derivable from any API.
 */
export const useSoloStepCompletion = (onboardingStatus?: OnboardingStatus) => {
  const { data: vehicles, isLoading: isLoadingVehicles } = useGetVehiclesQuery(vehicleStepParams);
  const { data: clients, isLoading: isLoadingClients } = useGetClientsQuery(clientStepParams);

  const completionByStep = useMemo<Record<number, boolean>>(
    () => ({
      3: isVehicleStepComplete(vehicles),
      4: isClientStepComplete(clients),
      5: isPricingStepComplete(onboardingStatus),
      6: isNetworkStepComplete(onboardingStatus),
    }),
    [vehicles, clients, onboardingStatus]
  );

  return {
    completionByStep,
    isLoading: isLoadingVehicles || isLoadingClients,
  };
};
